import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import React from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { patientsApi, PatientData } from '@/api/patients';
import { Floppy, Cross } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { READINESS_CHECKLIST_QUERY_KEY } from '@/hooks/use-readiness-checklist';
import { normalizeDateInputValue } from '@/lib/date';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture } from '@/lib/posthog';
import { toFormikValidate } from '@/lib/zod-formik-adapter';
import { Patient } from '@/types/patient';
import { masks } from '@/utils/masks';
import { toTitleCase } from '@/utils/toTitleCase';

interface PatientDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    patient: Patient | null;
    onPatientCreated?: (patient: Patient) => void;
    /**
     * Returns to whatever opened this dialog. Only set when there is somewhere to
     * go back to (the "Como você quer adicionar o paciente?" step) — editing a
     * patient from the list has no previous screen, so it renders no back control.
     */
    onBack?: () => void;
}

const maxBirthDate = format(new Date(), 'yyyy-MM-dd');

const patientSchema = z.object({
    full_name: z.string().min(1, 'Nome é obrigatório'),
    date_of_birth: z
        .string()
        .min(1, 'Data de nascimento é obrigatória')
        .refine((value) => value <= maxBirthDate, {
            message: 'Data de nascimento nao pode estar no futuro',
        }),
    gender: z.string().min(1, 'Sexo é obrigatório'),
    email: z.string().email('Email inválido').optional().or(z.literal('')),
    phone: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    education_level: z.string().optional(),
    referring_professional: z.string().optional(),
    emergency_contact_name: z.string().optional(),
    emergency_contact_phone: z.string().optional(),
});

export default function PatientDialog({
    open,
    onOpenChange,
    patient,
    onPatientCreated,
    onBack,
}: PatientDialogProps) {
    const queryClient = useQueryClient();
    const activePatientAccess = useFeatureEntitlement('patients.active');

    const createPatientMutation = useMutation({
        mutationFn: (data: PatientData) => patientsApi.create(data),
        onSuccess: (createdPatient) => {
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({
                queryKey: READINESS_CHECKLIST_QUERY_KEY,
            });
            queryClient.invalidateQueries({
                queryKey: ['patients', createdPatient.id, 'overview'],
            });
            toast.success('Paciente criado com sucesso');
            if (onPatientCreated) {
                onPatientCreated(createdPatient);
            }
            onOpenChange(false);
        },
        onError: (error) => {
            console.error(error);
            toast.error('Erro ao criar paciente');
        },
    });

    const updatePatientMutation = useMutation({
        mutationFn: (data: PatientData) => {
            if (!patient?.id) throw new Error('Patient ID is missing');
            return patientsApi.update(patient.id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            if (patient?.id) {
                queryClient.invalidateQueries({
                    queryKey: ['patients', patient.id, 'overview'],
                });
            }
            toast.success('Paciente atualizado com sucesso');
            onOpenChange(false);
        },
        onError: (error) => {
            console.error(error);
            toast.error('Erro ao atualizar paciente');
        },
    });

    const formik = useFormik({
        initialValues: {
            full_name: patient?.full_name || '',
            date_of_birth: normalizeDateInputValue(patient?.date_of_birth),
            gender: patient?.gender || '',
            email: patient?.email || '',
            phone: patient?.phone || patient?.contact_number || '',
            address: patient?.address || '',
            city: patient?.city || '',
            education_level: patient?.education_level || '',
            referring_professional: patient?.referring_professional || '',
            emergency_contact_name: patient?.emergency_contact_name || '',
            emergency_contact_phone: patient?.emergency_contact_phone || '',
        },
        enableReinitialize: true,
        validate: toFormikValidate(patientSchema),
        onSubmit: (values) => {
            const data: PatientData = {
                ...values,
                // Ensure empty strings are handled if necessary, but API likely handles them
            };

            if (patient) {
                capture('patient_form_submitted', { mode: 'edit' });
                updatePatientMutation.mutate(data);
            } else {
                if (activePatientAccess.shouldBlock) {
                    toast.warning(getEntitlementBlockedMessage(activePatientAccess.decision));
                    return;
                }

                // Deliberately before the request: the gap between this and the
                // server's `patient_created` is where validation failures live.
                capture('patient_form_submitted', { mode: 'create' });
                createPatientMutation.mutate(data);
            }
        },
    });

    const calculateAge = (dob: string): number | string => {
        if (!dob) return '';
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const currentAge = calculateAge(formik.values.date_of_birth);
    const isPending = createPatientMutation.isPending || updatePatientMutation.isPending;
    const { resetForm } = formik;

    // Reset form when dialog closes/opens
    React.useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open, patient, resetForm]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[calc(100svh-1rem)] w-[calc(100vw-1rem)] max-w-3xl overflow-hidden p-0 sm:max-h-[90vh] sm:w-full">
                <div className="flex max-h-[calc(100svh-1rem)] flex-col sm:max-h-[90vh]">
                    <div className="px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6">
                        {/* Back and title share one row, mirroring AppDialogLayout's
                            header so the two controls line up the same way here. */}
                        <div className="flex items-center">
                            {onBack ? (
                                <BackButton onClick={onBack} className="-ml-2 mr-2 shrink-0" />
                            ) : null}
                            <DialogHeader className="min-w-0 flex-1">
                                <DialogTitle>
                                    {patient ? 'Editar Paciente' : 'Adicionar Novo Paciente'}
                                </DialogTitle>
                                <DialogDescription className="sr-only">
                                    Preencha os dados do paciente e salve para concluir o cadastro.
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="flex min-h-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-4 sm:space-y-6 sm:px-6 sm:pb-6">
                            {/* Basic Information */}
                            <div>
                                <h3 className="mb-4 text-base font-semibold sm:text-lg">
                                    Informações Básicas
                                </h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="full_name">Nome Completo *</Label>
                                        <Input
                                            id="full_name"
                                            {...formik.getFieldProps('full_name')}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                formik.setFieldValue(
                                                    'full_name',
                                                    toTitleCase(e.target.value),
                                                );
                                            }}
                                            className={
                                                formik.touched.full_name && formik.errors.full_name
                                                    ? 'border-red-500'
                                                    : ''
                                            }
                                            placeholder="Digite o nome do paciente aqui"
                                        />
                                        {formik.touched.full_name && formik.errors.full_name && (
                                            <p className="text-sm text-red-500">
                                                {formik.errors.full_name}
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="date_of_birth">Data de Nascimento *</Label>
                                        <Input
                                            id="date_of_birth"
                                            type="date"
                                            max={maxBirthDate}
                                            {...formik.getFieldProps('date_of_birth')}
                                            className={
                                                formik.touched.date_of_birth &&
                                                formik.errors.date_of_birth
                                                    ? 'border-red-500'
                                                    : ''
                                            }
                                        />
                                        {formik.touched.date_of_birth &&
                                            formik.errors.date_of_birth && (
                                                <p className="text-sm text-red-500">
                                                    {formik.errors.date_of_birth}
                                                </p>
                                            )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="age">Idade</Label>
                                        <Input
                                            id="age"
                                            type="text"
                                            value={currentAge}
                                            readOnly
                                            disabled
                                            placeholder="Calculado automaticamente"
                                            className="cursor-not-allowed bg-muted"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="gender">Sexo *</Label>
                                        <Select
                                            value={formik.values.gender}
                                            onValueChange={(value: string) =>
                                                formik.setFieldValue('gender', value)
                                            }
                                        >
                                            <SelectTrigger
                                                className={
                                                    formik.touched.gender && formik.errors.gender
                                                        ? 'border-red-500'
                                                        : ''
                                                }
                                            >
                                                <SelectValue placeholder="Selecione o sexo" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="masculino">Masculino</SelectItem>
                                                <SelectItem value="feminino">Feminino</SelectItem>
                                                <SelectItem value="outro">Outro</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {formik.touched.gender && formik.errors.gender && (
                                            <p className="text-sm text-red-500">
                                                {formik.errors.gender}
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="referring_professional">Solicitante</Label>
                                        <Input
                                            id="referring_professional"
                                            {...formik.getFieldProps('referring_professional')}
                                            placeholder="Digite o nome do solicitante aqui"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="education_level">Escolaridade</Label>
                                        <Select
                                            value={formik.values?.education_level}
                                            onValueChange={(value: string) =>
                                                formik.setFieldValue('education_level', value)
                                            }
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Selecione a escolaridade" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="fundamental_incompleto">
                                                    Fundamental Incompleto
                                                </SelectItem>
                                                <SelectItem value="fundamental_completo">
                                                    Fundamental Completo
                                                </SelectItem>
                                                <SelectItem value="medio_incompleto">
                                                    Médio Incompleto
                                                </SelectItem>
                                                <SelectItem value="medio_completo">
                                                    Médio Completo
                                                </SelectItem>
                                                <SelectItem value="superior_incompleto">
                                                    Superior Incompleto
                                                </SelectItem>
                                                <SelectItem value="superior_completo">
                                                    Superior Completo
                                                </SelectItem>
                                                <SelectItem value="pos_graduacao">
                                                    Pós-Graduação
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div>
                                <h3 className="mb-4 text-base font-semibold sm:text-lg">
                                    Informações de Contato{' '}
                                    <span className="font-normal text-muted-foreground">
                                        (opcional)
                                    </span>
                                </h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            {...formik.getFieldProps('email')}
                                            className={
                                                formik.touched.email && formik.errors.email
                                                    ? 'border-red-500'
                                                    : ''
                                            }
                                            placeholder="nome@exemplo.com"
                                        />
                                        {formik.touched.email && formik.errors.email && (
                                            <p className="text-sm text-red-500">
                                                {formik.errors.email}
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Telefone</Label>
                                        <Input
                                            id="phone"
                                            {...formik.getFieldProps('phone')}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                formik.setFieldValue(
                                                    'phone',
                                                    masks.phone(e.target.value),
                                                );
                                            }}
                                            maxLength={15}
                                            placeholder="Digite o telefone aqui"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="city">Cidade</Label>
                                        <Input
                                            id="city"
                                            {...formik.getFieldProps('city')}
                                            placeholder="São Paulo"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="address">Endereço</Label>
                                        <Input
                                            id="address"
                                            {...formik.getFieldProps('address')}
                                            placeholder="Rua, Número, Bairro"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Emergency Contact */}
                            <div>
                                <h3 className="mb-4 text-base font-semibold sm:text-lg">
                                    Contato de Emergência{' '}
                                    <span className="font-normal text-muted-foreground">
                                        (opcional)
                                    </span>
                                </h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="emergency_contact_name">
                                            Nome do Contato
                                        </Label>
                                        <Input
                                            id="emergency_contact_name"
                                            {...formik.getFieldProps('emergency_contact_name')}
                                            placeholder="Digite o nome aqui"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="emergency_contact_phone">
                                            Telefone do Contato
                                        </Label>
                                        <Input
                                            id="emergency_contact_phone"
                                            {...formik.getFieldProps('emergency_contact_phone')}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                formik.setFieldValue(
                                                    'emergency_contact_phone',
                                                    masks.phone(e.target.value),
                                                );
                                            }}
                                            maxLength={15}
                                            placeholder="Digite o telefone aqui"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="border-t bg-muted/20 px-4 py-3 sm:px-6 sm:py-4">
                            <DialogFooter className="gap-2 sm:space-x-0">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={isPending}
                                    className="w-full sm:w-auto"
                                >
                                    <Cross />
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isPending}
                                    className="w-full sm:w-auto"
                                    aria-disabled={!patient && activePatientAccess.shouldBlock}
                                >
                                    <Floppy />
                                    {isPending
                                        ? 'Salvando...'
                                        : patient
                                          ? 'Atualizar Paciente'
                                          : 'Criar Paciente'}
                                    {!patient &&
                                    activePatientAccess.isUnavailableInPlan &&
                                    activePatientAccess.availableFromPlan ? (
                                        <FeatureUnavailableChip
                                            availablePlan={activePatientAccess.availableFromPlan}
                                        />
                                    ) : null}
                                </Button>
                            </DialogFooter>
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
